import { useState } from "react";
import { ArrowRight, Search } from "lucide-react";

import { CONNECTORS, CONNECTOR_CATEGORIES, logoURL, type Connector } from "@/lib/connectorCatalog";
import { Input } from "@/components/ui/input";

// ServiceLogo renders the brand mark in a white tile (so colored/dark logos read
// in both themes) and falls back to a brand-colored lettered tile if the image
// can't load — so the grid never shows a broken icon.
function ServiceLogo({ c }: { c: Connector }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-semibold text-white"
        style={{ backgroundColor: c.color }}
        aria-hidden
      >
        {c.name.slice(0, 1)}
      </span>
    );
  }
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-white">
      <img
        src={logoURL(c)}
        alt=""
        width={22}
        height={22}
        loading="lazy"
        className="h-[22px] w-[22px] object-contain"
        onError={() => setFailed(true)}
      />
    </span>
  );
}

function ConnectorCard({ c, onPick }: { c: Connector; onPick: (c: Connector) => void }) {
  return (
    <button
      type="button"
      onClick={() => onPick(c)}
      className="group flex items-center gap-3 rounded-xl border bg-card p-3.5 text-left transition-all hover:border-primary/40 hover:shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <ServiceLogo c={c} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate font-medium leading-tight">{c.name}</span>
          {c.needsOAuthApp && (
            <span className="shrink-0 rounded-full border border-warning/40 bg-warning/10 px-1.5 py-px text-[10px] font-medium text-warning">
              setup
            </span>
          )}
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">{c.blurb}</span>
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
}

// ConnectorCatalog is the browse-and-pick gallery. Selecting a service hands the
// Connector to onPick, which opens the Connect dialog prefilled with its name,
// suggested auth, and (where known) hosted MCP endpoint.
export function ConnectorCatalog({ onPick, compact }: { onPick: (c: Connector) => void; compact?: boolean }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const matches = (c: Connector) =>
    !query ||
    c.name.toLowerCase().includes(query) ||
    c.blurb.toLowerCase().includes(query) ||
    c.category.toLowerCase().includes(query);
  // Only show services that are connectable today (have a verified endpoint).
  const filtered = CONNECTORS.filter((c) => c.mcpUrl && matches(c));

  return (
    <div className="space-y-5">
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search connectors…"
          className="pl-9"
          aria-label="Search connectors"
        />
      </div>

      {CONNECTOR_CATEGORIES.map((cat) => {
        const items = filtered.filter((c) => c.category === cat);
        if (items.length === 0) return null;
        return (
          <section key={cat} className="space-y-2.5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{cat}</h3>
            <div className={compact ? "grid gap-2.5 sm:grid-cols-2" : "grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}>
              {items.map((c) => (
                <ConnectorCard key={c.id} c={c} onPick={onPick} />
              ))}
            </div>
          </section>
        );
      })}

      {filtered.length === 0 && (
        <p className="rounded-lg border border-dashed py-8 text-center text-sm text-muted-foreground">
          No connectors match “{q}”. You can still connect any integration by URL with the Connect integration button above.
        </p>
      )}
    </div>
  );
}
